import {
  ArrowBack,
  Cancel,
  CheckCircle,
  CurrencyRupee,
  Downloading,
  Engineering,
  Payment,
  Pending,
  Person,
  Receipt,
  Wallet,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { GETBookingById, UpdateBookingStatus } from '../../../services/booking';
import { useEffect, useState } from 'react';
import { IBooking } from '../../../utils/entities/interface';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { capitalizeWords, formatDate } from '../../../utils/helper';
import AssignTechnicianModal from './TechnicianModal';
import { BookingStatus } from '../../../utils/entities/type';
import { AddOrderItems } from '../../../components/common/AddOrderItem';
import useSnackbar from '../../../hooks/useSnackbar';
import { ViewBookedServices } from '../../../components/organisms/bsesrvices';
import { CustomerBookingDetails } from '../../../components/organisms/cbdetails';
import { ACServiceDetails } from '../../../components/organisms/acServiceReport';
import {
  canManageBookingInvoice,
  getInvoiceActionLabel,
  hasBookingInvoice,
} from '../../../utils/bookingInvoice';
import { DetailPageHeader } from '../../../components/layout/DetailPageHeader';
import { isGstAppliedOnBooking } from '../../../utils/invoiceGst';
import { getAssignBookingHeaderTitle } from '../../../utils/bookingNavigation';

interface OtherService {
  service: string;
  problem: string;
  comments: string;
  acType?: string;
}

interface AcList {
  services: string[];
  acList: Record<string, number>;
}

interface CopperPiping {
  comments: string;
  quantity: string;
  place: string;
}

export const AssignBooking = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const pageTitle = getAssignBookingHeaderTitle(location.state);
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [booking, setBooking] = useState<IBooking | null>(null);
  const [assignModal, setAssignModal] = useState<boolean>(false);
  const [cancelModal, setCancelModal] = useState<boolean>(false);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const [selectedSlot, setSelectedSlot] = useState<
    'FIRST_HALF' | 'SECOND_HALF'
  >('FIRST_HALF');
  const [basicService, setBasicService] =
    useState<Record<string, Record<string, number>>>();
  const [InvoiceModal, setInvoiceModal] = useState<boolean>(false);
  const [otherService, setOtherServices] = useState<OtherService | null>(null);
  const [copperpipingService, setCopperPipingService] =
    useState<CopperPiping | null>(null);
  const [amcservice, setAmcService] = useState<CopperPiping | null>(null);
  const [commercialAcService, setCommercialAcService] = useState<AcList | null>(
    null,
  );

  useEffect(() => {
    if (bookingId && !InvoiceModal) fetchBookingDetails(bookingId);
  }, [bookingId, InvoiceModal]);

  const fetchBookingDetails = async (id: string) => {
    try {
      const { data } = await GETBookingById(id);
      console.log('DATA', data.serviceDetails);
      const serviceAggregation: Record<string, Record<string, number>> = {};
      if (Array.isArray(data.serviceDetails)) {
        if (Array.isArray(data.serviceDetails)) {
          for (const details of data.serviceDetails) {
            const key = details.service_data?.key;
            if (!key) continue;
            // Skip saving if the service key matches any in the restricted list
            if (key == 'OTHER') {
              setOtherServices({
                service: key,
                problem: details.otherService,
                comments: details.comment,
                acType: details.acType || '',
              });
            }
            if (key === 'COMMERCIAL_AC') {
              // Filter only 'COMMERCIAL_AC' serviceDetails before reducing
              const filteredCommercialAc = data.serviceDetails.filter(
                (item) => item.service_data.key === 'COMMERCIAL_AC',
              );

              const acList: Record<string, number> =
                filteredCommercialAc.reduce(
                  (acc, { acType, quantity }) => {
                    acc[acType] = (acc[acType] || 0) + Number(quantity || 0);
                    return acc;
                  },
                  {} as Record<string, number>,
                );

              setCommercialAcService({
                acList,
                services: details.services, // Use current `details` instead of first index
              });
            }
            if (key === 'COPPER_PIPING') {
              setCopperPipingService({
                comments: details.comment,
                quantity: details.quantity,
                place: details.place,
              });
            }
            if (key === 'AMC') {
              setAmcService({
                comments: details.comment,
                quantity: details.quantity,
                place: details.place,
              });
            }
            if (
              ['COMMERCIAL_AC', 'COPPER_PIPING', 'AMC', 'OTHER'].includes(key)
            )
              continue;

            const acType = details.acType.toUpperCase(); // Normalize casing
            const quantity = parseInt(details.quantity, 10) || 0; // Ensure it's a number

            if (!serviceAggregation[key]) {
              serviceAggregation[key] = {};
            }

            if (!serviceAggregation[key][acType]) {
              serviceAggregation[key][acType] = 0;
            }

            serviceAggregation[key][acType] += quantity;
          }
        }
      }

      setBasicService(serviceAggregation);

      setBooking(data);
      setSelectedSlot(data.slot);
      // setShowComponent(data.status == 'BOOKED' ? 1 : 2);
    } catch (error) {
      console.error('Error fetching booking details:', error);
    }
  };
  const handleStatusChange = (status: BookingStatus) => {
    if (!bookingId) return;
    UpdateBookingStatus(bookingId, status)
      .then((responseStatus: number) => {
        if (responseStatus === 200) {
          fetchBookingDetails(bookingId);
          showSnackbar('Booking Moved to' + ' ' + status.replace('_', ' '));
        }
      })
      .catch((error: Error) => {
        throw Error(error.message);
      });
  };

  const handleCancelBooking = () => {
    if (!bookingId) return;

    const reason = cancelReason.trim();
    if (!reason) {
      showSnackbar('Please enter cancellation reason');
      return;
    }

    setIsCancelling(true);
    UpdateBookingStatus(bookingId, 'CANCELLED', reason)
      .then((responseStatus: number) => {
        if (responseStatus === 200) {
          setCancelModal(false);
          setCancelReason('');
          fetchBookingDetails(bookingId);
          showSnackbar('Booking cancelled successfully');
        }
      })
      .catch((error: Error) => {
        showSnackbar(error.message || 'Failed to cancel booking');
      })
      .finally(() => {
        setIsCancelling(false);
      });
  };

  const reloadBooking = () => {
    if (bookingId) {
      fetchBookingDetails(bookingId);
    }
  };

  const hasInvoice = hasBookingInvoice(booking);
  const canManageInvoice = canManageBookingInvoice(booking?.status, hasInvoice);
  const canCancelBooking =
    booking &&
    [
      'BOOKED',
      'ASSIGNMENT_PENDING',
      'TECHNICIAN_ASSIGNED',
      'RESCHEDULED',
      'IN_PROGRESS',
      'JOB_PENDING',
    ].includes(booking.status);

  return (
    <>
      {SnackbarComponent}
      <Dialog
        open={cancelModal}
        onClose={() => {
          if (!isCancelling) setCancelModal(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Please enter the reason for cancelling this booking.
          </Typography>
          <TextField
            fullWidth
            required
            multiline
            minRows={3}
            label="Cancellation Reason"
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelModal(false)}
            disabled={isCancelling}
          >
            Close
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelBooking}
            disabled={isCancelling || !cancelReason.trim()}
          >
            {isCancelling ? 'Cancelling...' : 'Cancel Booking'}
          </Button>
        </DialogActions>
      </Dialog>
      <AddOrderItems
        open={InvoiceModal}
        onClose={() => setInvoiceModal(false)}
        orderItems={
          booking && booking?.orderItems.length > 0 ? booking.orderItems : []
        }
        booking={booking ? booking : null}
        onReload={reloadBooking}
      />

      <DetailPageHeader
        title={pageTitle}
      />
      <Box
        sx={{
          backgroundColor: '#1C1C1C',
          color: '#fff',
          borderRadius: 2,
          m: 3,
        }}
      >
        <CustomerBookingDetails booking={booking} type={'VIEWDETAIL'} />
        {/* Service Details */}
        <ViewBookedServices
          basicService={basicService || null}
          otherService={otherService || null}
          copperpipingService={copperpipingService || null}
          amcservice={amcservice || null}
          commercialAcService={commercialAcService || null}
        />
        {/* <Divider sx={{ my: 2 }} /> */}
        {booking && (hasInvoice || canManageInvoice) && (
            <>
              {' '}
              {/* Service Details */}
              <Divider sx={{ borderColor: '#565656' }} />
              <Box
                sx={{
                  backgroundColor: 'transparent',
                  color: '#fff',
                  borderRadius: 2,
                  paddingBottom: 2,
                }}
              >
                <CardHeader
                  title={
                    <Typography variant="h6" sx={{ p: 1 }}>
                      Invoice
                    </Typography>
                  }
                  action={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {hasInvoice && booking.invoiceUrl ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            window.open(
                              booking.invoiceUrl,
                              '_blank',
                              'noopener,noreferrer',
                            )
                          }
                          sx={{ textTransform: 'none', color: '#fff' }}
                        >
                          View Invoice
                        </Button>
                      ) : null}
                      {canManageInvoice ? (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Receipt />}
                          onClick={() => setInvoiceModal(true)}
                          sx={{
                            textTransform: 'none',
                            backgroundColor: '#407BFF',
                            '&:hover': { backgroundColor: '#3366d6' },
                          }}
                        >
                          {getInvoiceActionLabel(hasInvoice)}
                        </Button>
                      ) : null}
                    </Box>
                  }
                />

                {hasInvoice ? (
                  <Invoice booking={booking} />
                ) : (
                  <Typography sx={{ color: '#8A8D91', px: 2, pb: 2 }}>
                    No invoice created yet. Create an invoice while the booking is in progress.
                  </Typography>
                )}
              </Box>{' '}
            </>
          )}
        {booking && <ACServiceDetails booking={booking} onReload={reloadBooking} />}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          m: 3,
          //   width: '90%',
        }}
      >
        {' '}
        {canCancelBooking && (
          <Button
            variant="outlined"
            sx={{
              color: '#D32F2F',
              borderColor: '#D32F2F',
              padding: '10px 20px',
              borderRadius: '14px',
              marginRight: 'auto',
              '&:hover': {
                borderColor: '#B71C1C',
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
              },
            }}
            startIcon={<Cancel />}
            onClick={() => setCancelModal(true)}
          >
            Cancel Booking
          </Button>
        )}
        {booking &&
          [
            'TECHNICIAN_ASSIGNED',
            'BOOKED',
            'RESCHEDULED',
            'ASSIGNMENT_PENDING',
          ].includes(booking?.status) && (
            <Button
              sx={{
                color: '#fff',
                backgroundColor: '#E31E25',
                '&:hover': { backgroundColor: '#E31E25' },
                padding: '10px 20px',
                borderRadius: '14px',
              }}
              startIcon={<Engineering />}
              //   onClick={handleCreateBooking}
              onClick={() => setAssignModal(true)}
            >
              {booking?.status == 'TECHNICIAN_ASSIGNED'
                ? ' Re-assign Technician'
                : ' Assign Technician'}
            </Button>
          )}
        {booking?.status == 'TECHNICIAN_ASSIGNED' && (
          <Button
            sx={{
              color: '#fff',
              backgroundColor: '#FFAA01',
              '&:hover': { backgroundColor: '#FFAA01' },
              padding: '10px 20px',
              borderRadius: '14px',
              marginLeft: 2,
            }}
            startIcon={
              <Downloading
                sx={{
                  // fontSize: 30,
                  color: '#fff',
                  transform: 'rotate(180deg)', // flips the icon vertically
                }}
              />
            }
            //   onClick={handleCreateBooking}
            onClick={() => handleStatusChange('IN_PROGRESS')}
          >
            Mark in Progress
          </Button>
        )}
        {booking?.status == 'IN_PROGRESS' && (
          <Button
            sx={{
              color: '#fff',
              backgroundColor: '#FFAA01',
              '&:hover': { backgroundColor: '#FFAA01' },
              padding: '10px 20px',
              borderRadius: '14px',
              marginLeft: 2,
            }}
            startIcon={
              <Downloading
                sx={{
                  // fontSize: 30,
                  color: '#fff',
                  transform: 'rotate(180deg)', // flips the icon vertically
                }}
              />
            }
            //   onClick={handleCreateBooking}
            onClick={() => handleStatusChange('JOB_PENDING')}
          >
            Mark as Pending
          </Button>
        )}
        {booking &&
          ['IN_PROGRESS', 'JOB_PENDING'].includes(booking?.status) && (
            <Button
              sx={{
                color: '#fff',
                backgroundColor: '#407BFF',
                '&:hover': { backgroundColor: '#407BFF' },
                padding: '10px 20px',
                borderRadius: '14px',
                marginLeft: 2,
              }}
              startIcon={<CheckCircle />}
              //   onClick={handleCreateBooking}
              onClick={() => handleStatusChange('COMPLETE')}
            >
              Completed Job
            </Button>
          )}
        {booking?.status == 'COMPLETE' && (
          <Button
            sx={{
              color: '#fff',
              backgroundColor: '#FF5934',
              '&:hover': { backgroundColor: '#FF5934' },
              padding: '10px 20px',
              borderRadius: '14px',
              marginLeft: 2,
            }}
            startIcon={<Wallet />}
            //   onClick={handleCreateBooking}
            onClick={() => handleStatusChange('PAYMENT_PENDING')}
          >
            Payment Pending
          </Button>
        )}
        {booking?.status === 'PAYMENT_PENDING' && (
          <Button
            sx={{
              color: '#fff',
              backgroundColor: '#0C9005',
              padding: '10px 20px',
              borderRadius: '14px',
              marginLeft: 2,
              '&:hover': {
                backgroundColor: '#0C9005', // slightly darker green on hover
              },
            }}
            startIcon={<Person />}
            onClick={() => {
              handleStatusChange('PAID');
              // setTimeout(() => {
              //   navigate(-1);
              // }, 500);
            }}
          >
            Paid
          </Button>
        )}
        <AssignTechnicianModal
          isOpen={assignModal}
          onClose={() => setAssignModal(false)}
          bookingId={bookingId ? bookingId : ''}
          onReload={reloadBooking}
          isEnquiry={false}
        />
      </Box>
    </>
  );
};

export const Invoice = ({ booking }: { booking: IBooking }) => {
  const gstApplied = isGstAppliedOnBooking(booking);
  const gstRate = gstApplied ? Number(booking?.tax?.gst || 0) : 0;
  const discountAmount = Number(booking?.discountAmount || 0);
  const invoiceType = booking?.invoiceType || 'STANDARD';

  return (
    <Box
      sx={{
        margin: 'auto',
        padding: 1,
        background: '#1C1C1C',
        color: '#fff',
        borderRadius: 7,
        border: '0.5px solid red',
        mx: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          // paddingBottom: 1,
          // marginBottom: 2,
        }}
      >
        <Box sx={{ padding: 2 }}>
          <Typography variant="h6" color="red">
            Bill To:
          </Typography>
          <Typography sx={{ color: '#FFFFFF' }}>{booking.user.name}</Typography>
          <Typography sx={{ color: '#FFFFFF' }}>
            {'Phone:' + ' ' + '+91' + booking.user.phoneNumber}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', padding: 2 }}>
          <Typography variant="h6" color="red">
            Invoice Details
          </Typography>
          <Typography sx={{ color: '#FFFFFF' }}>
            Invoice ID: {booking.bookingId}
          </Typography>
          <Typography sx={{ color: '#FFFFFF' }}>
            Type: {invoiceType.replace('_', ' ')}
          </Typography>
          <Typography sx={{ color: '#FFFFFF' }}>
            Date: {formatDate(booking.date)}
          </Typography>
        </Box>
      </Box>
      <TableContainer sx={{ background: 'transparent' }}>
        <Table>
          <TableHead sx={{ background: '#232323', textAlign: 'center' }}>
            <TableRow sx={{ textAlign: 'center' }}>
              <TableCell></TableCell>
              <TableCell sx={{ color: '#FFFFFF', textAlign: 'center' }}>
                Description
              </TableCell>
              <TableCell sx={{ color: '#FFFFFF', textAlign: 'center' }}>
                Quantity
              </TableCell>
              <TableCell sx={{ color: '#FFFFFF', textAlign: 'right' }}>
                Unit Price
              </TableCell>
              {gstApplied ? (
                <TableCell align="right">
                  <strong>GST ({gstRate}%)</strong>
                </TableCell>
              ) : null}
              <TableCell sx={{ color: '#FFFFFF', textAlign: 'right' }}>
                Total
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {booking.orderItems.map((orderitem: any, index) => {
              const quantity = Number(orderitem.quantity);
              const price = Number(orderitem.price);

              const subtotal = price * quantity;
              const gstAmount = gstApplied ? (subtotal * gstRate) / 100 : 0;
              const total = subtotal + gstAmount;

              return (
                <TableRow
                  key={index}
                  sx={{
                    '& td, & th': {
                      borderBottom: 'none',
                      verticalAlign: 'middle',
                    },
                    '& td:first-of-type, & th:first-of-type': {
                      textAlign: 'center',
                    },
                    '& td:nth-of-type(2)': {
                      textAlign: 'left',
                    },
                    '& td:nth-of-type(3)': {
                      textAlign: 'center',
                    },
                    '& td:nth-of-type(n+4)': {
                      textAlign: 'right',
                    },
                  }}
                >
                  <TableCell>{index + 1}</TableCell>

                  <TableCell>{orderitem.item}</TableCell>

                  <TableCell>{quantity.toString().padStart(2, '0')}</TableCell>

                  {/* UNIT PRICE */}
                  <TableCell>
                    <CurrencyRupee sx={{ fontSize: 16 }} />
                    {price.toFixed(2)}
                  </TableCell>

                  {gstApplied ? (
                    <TableCell>
                      <CurrencyRupee sx={{ fontSize: 16 }} />
                      {gstAmount.toFixed(2)}
                    </TableCell>
                  ) : null}

                  <TableCell>
                    <CurrencyRupee sx={{ fontSize: 16 }} />
                    {total.toFixed(2)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          textAlign: 'right',
          marginTop: 2,
          padding: 2,
        }}
      >
        {discountAmount > 0 ? (
          <Typography variant="body1" fontWeight="bold" textAlign="right" color="#0BE600">
            Discount:{' '}
            <CurrencyRupee sx={{ fontSize: 16, verticalAlign: 'middle' }} />
            {discountAmount.toFixed(2)}
          </Typography>
        ) : null}
        <Typography variant="h6" fontWeight="bold" textAlign="right">
          Total:{' '}
          <CurrencyRupee sx={{ fontSize: 20, verticalAlign: 'middle' }} />
          {Number(booking?.grandTotal || 0).toFixed(2)}
        </Typography>
      </Box>
    </Box>
  );
};

export const getBookingStatusLabel = (status: BookingStatus): string => {
  const statusMap: Record<BookingStatus, string> = {
    BOOKED: 'Booking',
    ASSIGNMENT_PENDING: 'Pending Assignment',
    TECHNICIAN_ASSIGNED: 'Technician Assigned',
    IN_PROGRESS: 'In Progress',
    PAYMENT_PENDING: 'Payment Pending',
    PAID: 'Paid',
    CANCELLED: 'Cancelled',
    ASSIGNED: 'Assigned',
    COMPLETE: 'Completed Job',
    DONE: 'Job Done',
    JOB_PENDING: 'Job Pending',
  };

  return statusMap[status] || status.replace('_', ' ');
};
